// src/lib/sentry.ts - Sentry initialization for Phase 4 monitoring
//
// Every Sentry egress hook is exported so tests exercise the exact functions
// initSentry() installs. Approvals B2 (D9–D11): each hook runs the pure
// review-capability redaction in ./telemetryRedaction over the whole payload,
// then (for events) the pre-existing PII scrub; Replay is not installed on the
// public `/review/*` route.
//
// Every hook FAILS CLOSED: if redaction throws (e.g. a throwing getter in the
// payload) events, transactions, breadcrumbs and Replay custom events are dropped
// (return null). A throwing beforeSend would otherwise make the SDK capture an
// internal exception that skips beforeSend entirely. beforeSendSpan cannot
// drop (10.51 sends the ORIGINAL span when it returns null), so it returns a
// minimal span with only ids/timestamps; beforeStartSpan falls back to a
// renamed copy.
import * as Sentry from "@sentry/react";

import { redactCapabilities } from "./telemetryRedaction";

import type {
  Breadcrumb,
  BreadcrumbHint,
  BrowserOptions,
  ErrorEvent,
  Event,
  EventHint,
  Scope,
} from "@sentry/react";

// Not re-exported by @sentry/react 10.51; derived from its public signatures
// so no import from the transitive @sentry/core is needed.
type TransactionEvent = Parameters<
  NonNullable<BrowserOptions["beforeSendTransaction"]>
>[0];
type StartSpanOptions = Parameters<typeof Sentry.startSpan>[0];
type SpanJSON = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0];
type Integration = Extract<
  NonNullable<BrowserOptions["integrations"]>,
  readonly unknown[]
>[number];

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_API_ENVIRONMENT || "development";

/**
 * Existing PII scrub (kept exactly as it was — design D11): removes
 * `user.email` / `user.ip_address` and five PII keys from breadcrumb data.
 * Mutates and returns `event`, which is always a fresh redacted copy here.
 */
function scrubPii<E extends Event>(event: E): E {
  // Remove user email and IP address
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
  }

  // Redact potentially sensitive breadcrumb data
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => {
      if (crumb.data) {
        // Remove any keys that might contain PII
        const sanitized = { ...crumb.data };
        ["email", "phone", "ssn", "card", "iban"].forEach((key) => {
          if (key in sanitized) delete sanitized[key];
        });
        return { ...crumb, data: sanitized };
      }
      return crumb;
    });
  }

  return event;
}

/**
 * `beforeSend`: redact review capabilities across the WHOLE error event
 * (request, transaction, message, exception, breadcrumbs, contexts, extra,
 * tags — design §1c P1–P6), then apply the existing PII scrub.
 */
export function sentryBeforeSend(
  event: ErrorEvent,
  _hint: EventHint,
): ErrorEvent | null {
  try {
    return scrubPii(redactCapabilities(event));
  } catch {
    return null;
  }
}

/**
 * `beforeSendTransaction`: the same whole-event redaction for transactions
 * (name, request.url, spans[].description / data), then the PII scrub.
 */
export function sentryBeforeSendTransaction(
  event: TransactionEvent,
  _hint: EventHint,
): TransactionEvent | null {
  try {
    return scrubPii(redactCapabilities(event));
  } catch {
    return null;
  }
}

/**
 * `beforeBreadcrumb`: redact at capture so the breadcrumb buffer never holds a
 * capability (navigation from/to, logger `route`, messages — P3, P5).
 */
export function sentryBeforeBreadcrumb(
  breadcrumb: Breadcrumb,
  _hint?: BreadcrumbHint,
): Breadcrumb | null {
  try {
    return redactCapabilities(breadcrumb);
  } catch {
    return null;
  }
}

/**
 * `browserTracingIntegration({ beforeStartSpan })`: rename pageload /
 * navigation spans before they start, which also keeps the token out of
 * `baggage: sentry-transaction=…` (P4, P10).
 */
export function sentryBeforeStartSpan(
  options: StartSpanOptions,
): StartSpanOptions {
  // "preserve": options may carry SDK Scope/Span instances the SDK needs back.
  try {
    return redactCapabilities(options, { opaque: "preserve" });
  } catch {
    try {
      return { ...options, name: redactCapabilities(options.name) };
    } catch {
      return { name: "[redacted]" };
    }
  }
}

/** Reads one primitive field of a span without letting a throwing getter escape. */
function safeSpanField(span: SpanJSON, key: keyof SpanJSON): unknown {
  try {
    const v = span[key];
    if (typeof v === "string") return redactCapabilities(v);
    return typeof v === "number" ? v : undefined;
  } catch {
    return undefined;
  }
}

/**
 * `beforeSendSpan`: standalone spans (INP and other web-vital spans) are sent
 * through createSpanEnvelope and never reach beforeSendTransaction. Their
 * `data.transaction` is the route name, which can be the raw
 * `/review/<token>` (review CRITICAL 1). Returning null does NOT drop a span
 * in 10.51 — the SDK sends the original — so the failure path returns a
 * minimal span carrying only ids and timestamps.
 */
export function sentryBeforeSendSpan(span: SpanJSON): SpanJSON {
  try {
    return redactCapabilities(span);
  } catch {
    return {
      span_id: (safeSpanField(span, "span_id") as string) ?? "",
      trace_id: (safeSpanField(span, "trace_id") as string) ?? "",
      parent_span_id: safeSpanField(span, "parent_span_id") as
        | string
        | undefined,
      start_timestamp: (safeSpanField(span, "start_timestamp") as number) ?? 0,
      timestamp: safeSpanField(span, "timestamp") as number | undefined,
      op: safeSpanField(span, "op") as string | undefined,
      description: "[redacted]",
      data: {},
    };
  }
}

/**
 * Scope listener that keeps the scope's transaction name redacted. The SDK's
 * startBrowserTracingPageLoadSpan / NavigationSpan call
 * `getCurrentScope().setTransactionName(<raw name>)` AFTER beforeStartSpan ran
 * on a copy, and INP spans fall back to that scope name once the pageload
 * span has ended. `setTransactionName` notifies listeners synchronously, and
 * the SDK's re-entrancy guard means the rewrite below does not recurse, so
 * the raw name never survives the call that set it.
 */
export function redactScopeTransactionName(scope: Scope): void {
  try {
    const name = scope.getScopeData().transactionName;
    if (typeof name !== "string") return;
    const redacted = redactCapabilities(name);
    if (redacted !== name) scope.setTransactionName(redacted);
  } catch {
    try {
      scope.setTransactionName(undefined);
    } catch {
      // Nothing further can be done without the scope.
    }
  }
}

/** True when `pathname` is the public review capability route (`/review` or `/review/...`). */
function isReviewCapabilityPath(pathname: string): boolean {
  return /^\/review(\/|$)/i.test(pathname);
}

/**
 * Replay `beforeAddRecordingEvent`: PARTIAL defense in depth only.
 *
 * What it sees: @sentry-internal/replay 10.51 calls this hook ONLY for rrweb
 * custom events (type 5) — `maybeApplyCallback` checks `isCustomEvent(event)`.
 * In practice those are the SDK's own `breadcrumb`, `performanceSpan` and
 * `options` frames. Full snapshots (2), incremental DOM mutations (3) and
 * Meta events (4, which carry `data.href`) are never routed here, and neither
 * is `replay_event.urls`. This hook cannot redact any of them.
 *
 * The complete protection on the capability page is D10: buildIntegrations()
 * adds no Replay integration when initSentry() runs on `/review/*`.
 *
 * Depth: the default REDACTION_MAX_DEPTH (20) is used. A breadcrumb frame is
 * `{type, timestamp, data: {tag, payload}}` where the SDK has already
 * normalized `payload` to depth 10 (`normalize(breadcrumb, 10, 1000)`), so
 * the deepest custom event is about 12 levels. Anything deeper still fails
 * closed to a marker.
 *
 * Path guard: while `location.pathname` is `/review/*` (only reachable with
 * Replay installed through in-app navigation, which no route does today)
 * custom events are dropped. That does not stop the snapshot, incremental or
 * Meta events above.
 */
function replayBeforeAddRecordingEvent<E>(event: E): E | null {
  try {
    if (isReviewCapabilityPath(window.location.pathname)) return null;
    return redactCapabilities(event);
  } catch {
    return null;
  }
}

/**
 * Integrations for the page being initialized (design D10). On the public
 * review route no Replay integration is added at all: nothing scrubs
 * `replay_event.urls`, so not recording is the only complete defense, and
 * that route is only ever reached by a fresh load of the link. Everywhere
 * else Replay runs with redaction on the custom events the SDK routes
 * through beforeAddRecordingEvent (not snapshots or DOM mutations).
 */
export function buildIntegrations(pathname: string): Integration[] {
  const integrations: Integration[] = [
    Sentry.browserTracingIntegration({
      beforeStartSpan: sentryBeforeStartSpan,
    }),
  ];
  if (!isReviewCapabilityPath(pathname)) {
    integrations.push(
      Sentry.replayIntegration({
        // Don't record PII in session replays
        maskAllText: true,
        blockAllMedia: true,
        beforeAddRecordingEvent: replayBeforeAddRecordingEvent,
      }),
    );
  }
  return integrations;
}

export function initSentry() {
  // Skip if DSN is not configured or is still using placeholder
  if (!SENTRY_DSN || SENTRY_DSN === "your_sentry_dsn_here") {
    console.warn("Sentry DSN not configured - monitoring disabled");
    return;
  }

  // Registered BEFORE init: init starts the pageload span, which writes the raw
  // route name onto the current scope.
  Sentry.getCurrentScope().addScopeListener(redactScopeTransactionName);
  Sentry.getIsolationScope().addScopeListener(redactScopeTransactionName);

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: import.meta.env.VITE_GIT_SHA || undefined,
    // First production activation is error events only (Dave, 2026-09-25;
    // registry production_sentry_activation): no performance traces and no
    // Session Replay, for sessions or for errors. Raising any of these rates
    // is a separate, deliberate decision.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Send only from exactly "production". Previews, staging and anything
    // unnamed stay silent until they get their own environment name and an
    // intentional destination.
    enabled: ENVIRONMENT === "production",

    // Redact review capabilities, then strip PII, before anything leaves
    beforeSend: sentryBeforeSend,
    beforeSendTransaction: sentryBeforeSendTransaction,
    beforeBreadcrumb: sentryBeforeBreadcrumb,
    beforeSendSpan: sentryBeforeSendSpan,

    // Integration configuration (Replay omitted on /review/* — D10)
    integrations: buildIntegrations(window.location.pathname),
  });
}

/**
 * Report rate limit exceeded (429) events to Sentry for telemetry
 * Usage: call from edge handlers when rate limit is hit
 */
export function reportRateLimitExceeded(
  route: string,
  context?: Record<string, unknown>,
) {
  Sentry.captureMessage("rate_limit_exceeded", {
    level: "warning",
    tags: {
      component: "edge",
      route,
    },
    extra: context
      ? {
          // Only include non-PII context
          ...Object.fromEntries(
            Object.entries(context).filter(
              ([key]) =>
                !["email", "phone", "user_id", "ip_address"].includes(key),
            ),
          ),
        }
      : undefined,
  });
}

/**
 * Capture sanitization events for security monitoring
 * Usage: call when prompt injection or XSS is detected
 */
export function reportSecurityEvent(
  type: "prompt_injection" | "xss_attempt" | "pii_detected",
  context?: Record<string, unknown>,
) {
  Sentry.captureMessage(`security_event_${type}`, {
    level: "warning",
    tags: {
      component: "privacy",
      security_event: type,
    },
    extra: context,
  });
}

/**
 * Report a NextMove row written with `move_type: null` — the coverage
 * signal that asks "what % of NextMove writes are still emitting null
 * move_type in production?". Fires alongside the existing console.warn
 * so local dev keeps its visible signal; this gives Slice 5's flag flip
 * a durable at-scale gate that survives outside the SPA browser context.
 *
 * Level is "info", not warning — Phase A null is the design today, not
 * an error. The signal becomes a coverage metric, not an alert.
 *
 * No PII: payload carries vocabulary literals (recommendation_type,
 * intent_type, channel_hint, reason_code) and a client_id. Donor / user
 * identifiers are not in scope.
 *
 * Spec ties: `nextmove_unmapped_warn_observability` registry entry,
 * sibling to `nextmove_decision_frame_ui` Slice 5.
 */
export function reportNextMoveUnmappedMoveType(payload: {
  recommendation_type: string | null;
  intent_type: string | null;
  channel_hint: string | null;
  reason_code: string | null;
  client_id: string;
}) {
  Sentry.captureMessage("next_move_move_type_unmapped", {
    level: "info",
    tags: {
      component: "next_move",
      coverage_event: "move_type_unmapped",
    },
    extra: payload,
  });
}
